import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";

import {
  Button,
  DataError,
  LoadingIndicator,
  Typography
} from "@digital-services/ui";
import { shortenOrgName } from "@ds-cps/ui";

import { useGetOrganization } from "@organization/services/use-get-organization";

import CheckIcon from "@icons/checkCircle.svg?react";
import RightArrow from "@icons/right-arrow.svg?react";

interface Props {
  organizationId: string;
  onClose: () => void;
}

export const OrganizationCreatedMessage = (props: Props) => {
  const { organizationId, onClose } = props;

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const { organization, isLoading, error } = useGetOrganization(organizationId);

  const { t } = useTranslation(undefined, { keyPrefix: "organization" });
  const { t: tCommon } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  const msg = t("create.success.message", {
    organization: shortenOrgName(organization.name)
  });

  return (
    <div className="flex flex-col" data-testid="organization-save-message">
      <div
        data-testid="organization-success-message"
        className="h-min[100px] flex flex-row gap-3"
      >
        <div data-testid="check-icon">
          <CheckIcon />
        </div>
        <Typography variant="description">
          {organization.name.length > 50 ? (
            <span title={organization.name} className="cursor-help">
              {msg}
            </span>
          ) : (
            msg
          )}
        </Typography>
      </div>
      <div
        data-testid="info-message"
        className="h-min[100px] flex flex-row gap-3 pt-5"
      >
        <div data-testid="arrow-icon">
          <RightArrow />
        </div>
        <Typography
          variant="sectionTitle"
          className="cursor-pointer"
          data-testid="link-to-organization-page"
        >
          <NavLink
            to={`organizations/${organizationId}/applications`}
            onClick={onClose}
          >
            {t("create.info.message")}
          </NavLink>
        </Typography>
      </div>
      <div className="flex flex-row-reverse pt-5">
        <Button
          data-testid="organization-created-close"
          isLoading={isBusy}
          onClick={() => {
            setIsBusy(true);
            onClose();
          }}
        >
          {tCommon("button.close.label")}
        </Button>
      </div>
    </div>
  );
};
